// VideoCommentComposer.tsx — top-level comment composer for the video watch page.
// Signed-out → SignInCta. Visual language mirrors post-detail's CommentComposer;
// deliberately its own component (no shared import) to avoid coupling to the
// post-comment feature.
import { useState } from 'react';
import type { ChangeEvent, KeyboardEvent } from 'react';
import styled from 'styled-components';
import { ConsolePanel, Avatar, Button } from '@/ui';
import { useSession } from '@/hooks';
import { SignInCta } from '@/components/SignInCta';

export interface VideoCommentComposerProps {
  onSubmit: (body: string) => void;
}

const Wrap = styled(ConsolePanel)`
  display: flex;
  gap: 12px;
  align-items: flex-start;
  padding: 14px 16px;
`;

const Col = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const Input = styled.textarea`
  width: 100%;
  min-height: 56px;
  resize: none;
  background: transparent;
  border: none;
  border-bottom: 1px solid ${({ theme }) => theme.color.glassBorder};
  color: ${({ theme }) => theme.color.textPrimary};
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.md};
  outline: none;
  &::placeholder {
    color: ${({ theme }) => theme.color.textMuted};
  }
  &:focus-visible {
    border-bottom-color: ${({ theme }) => theme.color.accentCyan};
  }
`;

const Actions = styled.div`
  display: flex;
  justify-content: flex-end;
`;

export function VideoCommentComposer({ onSubmit }: VideoCommentComposerProps) {
  const { isAuthenticated, currentUser } = useSession();
  const [value, setValue] = useState('');
  const trimmed = value.trim();

  if (!isAuthenticated) {
    return <SignInCta message="Sign in to join the conversation." />;
  }

  function submit() {
    if (!trimmed) return;
    onSubmit(trimmed);
    setValue('');
  }

  function keydown(e: KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      submit();
    }
  }

  return (
    <Wrap>
      <Avatar name={currentUser?.name ?? 'User'} size={40} />
      <Col>
        <Input
          value={value}
          onChange={(e: ChangeEvent<HTMLTextAreaElement>) => setValue(e.target.value)}
          onKeyDown={keydown}
          placeholder="Add a comment…"
          aria-label="Add a comment"
        />
        <Actions>
          <Button variant="primary" disabled={!trimmed} onClick={submit}>
            Post
          </Button>
        </Actions>
      </Col>
    </Wrap>
  );
}
